import { varIs, varKey, varLabel } from "common/var";
import {
  CountryFlag,
  RankBadge,
  Select,
  TablePanel,
  UserAvatar,
} from "components/common";
import { useEffect, useState } from "react";
import {
  FaFacebookMessenger,
  FaTelegramPlane,
  FaWhatsapp,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { callGetApiWithAuth } from "utils/api";
import { asDate, asVolume } from "utils/text";
import UplineBar from "./UplineBar";

export default function ListTable(props) {
  const myUser = useSelector((state) => state.auth.user);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uplines, setUplines] = useState([]);

  const [paginationParam, setPaginationParam] = useState({
    currentPage: 1,
    perPage: 15,
    total: 0,
  });

  const [searchParam, setSearchParam] = useState({
    status: "",
    type: "",
    sort_by: "op",
    filter_month: "current",
  });

  const onGetTableData = (res) => {
    setTableData(res?.data?.childrenData?.data ?? []);
    setPaginationParam({
      ...paginationParam,
      currentPage: res?.data?.childrenData?.current_page ?? 1,
      total: res?.data?.childrenData?.total ?? 0,
    });
    setUplines(res?.data?.uplines ?? []);
    setIsLoading(false);
  };

  const onFailTableData = () => setIsLoading(false);

  const loadTableData = (paginationParam0, searchParam0) => {
    setIsLoading(true);
    const params = {
      user_id: props.selectedId,
      ...searchParam0,
      page: paginationParam0.currentPage,
      per_page: paginationParam0.perPage,
    };
    const q = Object.keys(params)
      .map((k) => `${k}=${params[k]}`)
      .join("&");

    const endpoint =
      searchParam.filter_month === "current"
        ? `my_team/placement_list/list?${q}`
        : `my_team/placement_list/last_list?${q}`;

    callGetApiWithAuth(endpoint, onGetTableData, onFailTableData);
  };

  const onPageChange = (page) => {
    const paginationParam0 = { ...paginationParam, currentPage: page };
    loadTableData(paginationParam0, searchParam);
  };

  useEffect(() => {
    if (myUser) {
      const paginationParam0 = { ...paginationParam, currentPage: 1 };
      setPaginationParam(paginationParam0);
      loadTableData(paginationParam0, searchParam);
    }
  }, [myUser, props.selectedId, searchParam]);

  const renderChatIcon = (messenger, IconComponent, linkKey, enabledKey) => {
    if (messenger?.[enabledKey] * 1 === 1 && messenger?.[linkKey]) {
      return (
        <span className="chat-icon">
          <IconComponent
            onClick={() => window.open(messenger[linkKey], "_blank")}
          />
        </span>
      );
    }
    return null;
  };

  return (
    <TablePanel
      title={
        <div className="enrollerList d-flex align-items-center gap-1">
          Placement List
          <span style={{ width: 8 }} />
          <StatusSelectWrapper>
            <Select
              value={searchParam.status}
              options={[
                { label: "All", value: "" },
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
                { label: "Closed", value: "closed" },
                { label: "Purged", value: "purged" },
              ]}
              onChange={(v) => setSearchParam({ ...searchParam, status: v })}
            />
          </StatusSelectWrapper>
          <span style={{ width: 8 }} />
          <StatusSelectWrapper>
            <Select
              value={searchParam.type}
              options={[
                { label: "SM & Customer", value: "" },
                { label: "Social Marketer", value: "social_marketer" },
                { label: "Customer", value: "customer" },
              ]}
              onChange={(v) => setSearchParam({ ...searchParam, type: v })}
            />
          </StatusSelectWrapper>
          <span style={{ width: 8 }} />
          <label>Sort By</label>
          <span style={{ width: 4 }} />
          <StatusSelectWrapper>
            <Select
              value={searchParam.sort_by}
              options={[
                { label: "OP", value: "op" },
                { label: "Promoter Rank", value: "rank_id" },
                { label: "Builder Rank", value: "builder_rank_id" },
              ]}
              onChange={(v) => setSearchParam({ ...searchParam, sort_by: v })}
            />
          </StatusSelectWrapper>
          <span style={{ width: 8 }} />
          <label>Month:</label>
          <span style={{ width: 4 }} />
          <StatusSelectWrapper>
            <Select
              value={searchParam.filter_month}
              options={[
                { label: "Current", value: "current" },
                { label: "Last", value: "last" },
              ]}
              onChange={(v) => setSearchParam({ ...searchParam, filter_month: v })}
            />
          </StatusSelectWrapper>
        </div>
      }
      data={tableData}
      loading={isLoading}
      paginationParam={paginationParam}
      onPageChange={onPageChange}
      toolbar={<UplineBar uplines={uplines} goUser={props.goUser} />}
      columns={[
        {
          title: "ID",
          key: "uuid",
          render: (_, record) => <span>#{record?.uuid ?? ""}</span>,
        },
        {
          title: "Level",
          key: "level",
          render: (_, record) => <span>{record?.level ?? 0}</span>,
        },
        {
          title: "Username",
          key: "username",
          render: (_, record) => (
            <div className="d-flex align-items-center" style={{ color: "#0ABAB5", fontWeight: 600 }}>
              {record?.has_children ? (
                <a href="#" onClick={() => props.goUser(record)}>
                  {record?.username ?? ""}
                </a>
              ) : (
                <span>{record?.username ?? ""}</span>
              )}
            </div>
          ),
        },
        {
          title: "Name",
          key: "name",
          render: (_, record) => (
            <div className="d-flex align-items-center" style={{ minWidth: "200px" }}>
              <UserAvatar
                image={record?.image}
                title={`${record?.first_name ?? ""} ${record?.last_name ?? ""}`}
                status={varKey("user.status", record?.status)}
                type={varKey("user.type", record?.type)}
                typeLabel={varLabel("user.type", record?.type)}
                joinDate={asDate(record?.created_at)}
              />
              {renderChatIcon(record?.social_messenger, FaFacebookMessenger, "facebook_link", "facebook_enabled")}
              {renderChatIcon(record?.social_messenger, FaTelegramPlane, "telegram_link", "telegram_enabled")}
              {renderChatIcon(record?.social_messenger, FaWhatsapp, "whatsapp_link", "whatsapp_enabled")}
            </div>
          ),
        },
        {
          title: "Country",
          key: "country",
          render: (_, record) => <CountryFlag country={record?.country ?? ""} />,
        },
        {
          title: "Enroller",
          key: "enroller",
          render: (_, record) => <span>{record?.enroller?.username ?? ""}</span>,
        },
        {
          title: "Parent",
          key: "parent",
          render: (_, record) => <span>{record?.parent?.username ?? ""}</span>,
        },
        {
          title: "Placement",
          key: "placement",
          render: (_, record) => <span>{record?.placement?.username ?? ""}</span>,
        },
        {
          title: "Promoter Rank",
          key: "rank_id",
          render: (_, record) =>
            varIs("user.type", record?.type, "affiliate") ? (
              <RankBadge label={record?.rank?.name ?? ""} />
            ) : (
              ""
            ),
        },
        {
          title: "Builder Rank",
          key: "builder_rank_id",
          render: (_, record) =>
            varIs("user.type", record?.type, "affiliate") ? (
              <RankBadge label={record?.builder_rank?.name ?? ""} />
            ) : (
              ""
            ),
        },
        {
          title: "Subscription",
          key: "subscription",
          render: (_, record) => (
            <span>
              {record?.subscription_count * 1 > 0 ? (
                <span className="subscription-yes">Yes</span>
              ) : (
                <span className="subscription-no">No</span>
              )}
            </span>
          ),
        },
        {
          title: "MP",
          key: "mp",
          render: (_, record) => (
            <span>
              {asVolume(
                searchParam.filter_month === "current"
                  ? record?.qualification?.mp ?? 0
                  : record?.mp ?? 0
              )}
            </span>
          ),
        },
        {
          title: "MP (on Subscriptions)",
          key: "c_mp",
          render: (_, record) => (
            <span>
              {asVolume(
                searchParam.filter_month === "current"
                  ? record?.qualification?.c_mp ?? 0
                  : record?.c_mp ?? 0
              )}
            </span>
          ),
        },
        {
          title: "AP",
          key: "ap",
          render: (_, record) => (
            <span>
              {asVolume(
                searchParam.filter_month === "current"
                  ? record?.qualification?.ap ?? 0
                  : record?.ap ?? 0
              )}
            </span>
          ),
        },
        {
          title: "OP",
          key: "op",
          render: (_, record) => (
            <span>
              {asVolume(
                searchParam.filter_month === "current"
                  ? record?.qualification?.op ?? 0
                  : record?.op ?? 0
              )}
            </span>
          ),
        },
        {
          title: "Promoter Rank Tracker",
          key: "promoter_rank_tracker",
          render: (_, record) => (
            <a
              className="text-decoration-underline"
              href="#"
              onClick={() => props.onClickPRTracker(record?.uuid)}
            >
              View Details
            </a>
          ),
        },
        {
          title: "Builder Rank Tracker",
          key: "builder_rank_tracker",
          render: (_, record) => (
            <a
              className="text-decoration-underline"
              href="#"
              onClick={() => props.onClickBRTracker(record?.uuid)}
            >
              View Details
            </a>
          ),
        },
      ]}
    />
  );
}

const StatusSelectWrapper = styled.div`
  width: 200px;
  .ant-select {
    width: 100%;
  }
  .ant-select-selector {
    height: 44px !important;
  }
`;
